import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ToastTone = "ok" | "err" | "info";

type Toast = { id: number; text: string; tone: ToastTone };

type ToastCtx = {
  toast: (text: string, tone?: ToastTone) => void;
};

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);

  const toast = useCallback((text: string, tone: ToastTone = "info") => {
    seq.current += 1;
    const id = seq.current;
    setItems((list) => [...list.slice(-3), { id, text, tone }]);
    window.setTimeout(() => {
      setItems((list) => list.filter((t) => t.id !== id));
    }, tone === "err" ? 6000 : 3500);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast toast-${t.tone}`}>
            {t.text}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error("ToastProvider missing");
  return v;
}
